/**
 * Room composition ⇄ characteristic-count reconciliation.
 *
 * The property *composition* (`PropertyCharacteristics.roomDetails`) is the
 * SINGLE SOURCE OF TRUTH for residential room counts. The scalar count fields
 * (`characteristics.bedrooms`, `.bathrooms`, …) are a derived cache of it.
 *
 * Historically the two could drift: the listing-quality page wrote the scalar
 * alone, so a property could show "6 bedrooms" in Characteristics while the
 * Composition page (driven purely by `roomDetails`) showed none. These pure
 * helpers keep the two representations reconciled from a single, curated map so
 * that surface code (composition page, listing-quality inline edits) can never
 * disagree about which count means which room type.
 */

import { RoomDetail } from './property-room-details';

/**
 * Canonical, UNAMBIGUOUS 1:1 mapping between a scalar count field on
 * `PropertyCharacteristics` and the `roomDetails.roomType` it materialises.
 *
 * Counts fed by more than one room type are intentionally excluded — a single
 * scalar can't be reconciled back to one room type, so those stay
 * `roomDetails`-driven only. Excluded on purpose:
 *  • `balconies`   ← `balcony` AND `balcony_terrace`
 *  • `officeSpaces`← `office`  AND `office_space`
 */
export const COUNT_FIELD_TO_ROOM_TYPE: Readonly<Record<string, string>> = {
  bedrooms: 'bedroom',
  bathrooms: 'bathroom',
  kitchens: 'kitchen',
  livingRooms: 'living_room',
  diningRooms: 'dining_room',
  laundryRooms: 'laundry',
  terraces: 'terrace',
  closets: 'closet',
  storageRooms: 'storage'
};

/** Reverse of {@link COUNT_FIELD_TO_ROOM_TYPE} — `roomType` → scalar count field. */
export const ROOM_TYPE_TO_COUNT_FIELD: Readonly<Record<string, string>> =
  Object.entries(COUNT_FIELD_TO_ROOM_TYPE).reduce<Record<string, string>>(
    (acc, [countField, roomType]) => {
      acc[roomType] = countField;
      return acc;
    },
    {}
  );

/** Factory for a fresh room id. Injected so the helpers stay pure/testable. */
export type RoomIdFactory = () => string;

/**
 * Minimal shape {@link materializeDeclaredCounts} reads: the composition plus the
 * dynamic scalar count fields. Kept loose (index signature) so callers can pass a
 * `PropertyCharacteristics` (via a cast) and tests can pass plain literals.
 */
export type RoomCompositionSource = { roomDetails?: RoomDetail[] | null } & Record<string, unknown>;

/** Count the rooms of a given `roomType` in a composition. */
export const countRoomsByType = (
  roomDetails: readonly RoomDetail[] | null | undefined,
  roomType: string
): number => (roomDetails ?? []).filter((r) => r.roomType === roomType).length;

/**
 * Parse an inline-edit dotted path (e.g. `characteristics.bedrooms`) and, when it
 * targets a count field that maps 1:1 to a composition room type, return that
 * pair. Returns `null` for every other path — the caller then writes the scalar
 * directly (status quo). Pure.
 */
export const roomCountTargetForPath = (
  path: string
): { countField: string; roomType: string } | null => {
  const parts = path.split('.');
  if (parts.length !== 2 || parts[0] !== 'characteristics') return null;
  const countField = parts[1];
  const roomType = COUNT_FIELD_TO_ROOM_TYPE[countField];
  return roomType ? { countField, roomType } : null;
};

/**
 * A "bare" room carries no descriptive content — only `id`/`roomType` with an
 * empty `name` and no `sqm` (the shape produced by quick-add). These are dropped
 * first when a count is lowered, so manually-enriched rooms survive longest.
 */
export const isBareRoom = (room: RoomDetail): boolean => {
  const BASE_KEYS = new Set(['id', 'roomType', 'name', 'sqm']);
  const hasName = typeof room.name === 'string' && room.name.trim().length > 0;
  const hasSqm = room.sqm !== null && room.sqm !== undefined;
  const hasExtra = Object.keys(room).some((key) => {
    if (BASE_KEYS.has(key)) return false;
    const value = room[key];
    return value !== null && value !== undefined && value !== '' && value !== false;
  });
  return !hasName && !hasSqm && !hasExtra;
};

/**
 * Reconcile a composition so it contains EXACTLY `targetCount` rooms of
 * `roomType`, making the composition the source of truth for that count:
 *  • short → append bare rooms (type only) until the target is reached;
 *  • over  → drop the least-valuable extras first (bare before enriched, newest
 *            before oldest), preserving everything else and its order;
 *  • equal → returned unchanged (as a fresh array reference).
 *
 * Pure and non-mutating. `targetCount` is floored at 0. Rooms of other types are
 * never touched and keep their relative order.
 */
export const reconcileRoomCount = (
  roomDetails: readonly RoomDetail[] | null | undefined,
  roomType: string,
  targetCount: number,
  makeId: RoomIdFactory
): RoomDetail[] => {
  const source = [...(roomDetails ?? [])];
  const safeTarget = Math.max(0, Math.floor(targetCount));
  const ofType = source.filter((r) => r.roomType === roomType);
  const current = ofType.length;

  if (current === safeTarget) return source;

  if (current < safeTarget) {
    const additions: RoomDetail[] = [];
    for (let i = 0; i < safeTarget - current; i++) {
      additions.push({ id: makeId(), roomType, name: '', sqm: null });
    }
    return [...source, ...additions];
  }

  // current > safeTarget: remove the (current − safeTarget) least-valuable rooms.
  const toRemove = current - safeTarget;
  const bareNewestFirst = ofType.filter(isBareRoom).reverse();
  const enrichedNewestFirst = ofType.filter((r) => !isBareRoom(r)).reverse();
  const removalOrder = [...bareNewestFirst, ...enrichedNewestFirst];
  const removeIds = new Set(removalOrder.slice(0, toRemove).map((r) => r.id));
  return source.filter((r) => !removeIds.has(r.id));
};

/**
 * Materialise every declared-but-unmaterialised mapped count into bare rooms so
 * that `roomDetails` becomes the single source of truth before any count is
 * re-derived from it. ADDITIVE ONLY — never removes a room and never lowers a
 * count, so it cannot lose data. Returns a new array (the original when nothing
 * needed materialising).
 *
 * This guards the composition write paths: re-deriving counts from `roomDetails`
 * after a quick-add/delete would otherwise zero out a count that was declared
 * (e.g. from the creation wizard) but never turned into rooms.
 */
export const materializeDeclaredCounts = (
  characteristics: RoomCompositionSource | null | undefined,
  makeId: RoomIdFactory
): RoomDetail[] => {
  let rooms: RoomDetail[] = [...(characteristics?.roomDetails ?? [])];
  if (!characteristics) return rooms;

  for (const [countField, roomType] of Object.entries(COUNT_FIELD_TO_ROOM_TYPE)) {
    const declared = Number(characteristics[countField] ?? 0);
    if (Number.isFinite(declared) && declared > countRoomsByType(rooms, roomType)) {
      rooms = reconcileRoomCount(rooms, roomType, declared, makeId);
    }
  }
  return rooms;
};
